import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatchData } from "./dispatch/useDispatchData";
import { useDispatchActions } from "./dispatch/useDispatchActions";
import { DispatchConsolePanel } from "./dispatch/DispatchConsolePanel";
import { DispatchCallCard } from "./dispatch/DispatchCallCard";
import { sortLiveCalls } from "./dispatch/dispatchCallUtils";
import DispatchChat from "./dispatch/DispatchChat";

function tsMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  return value instanceof Date ? value.getTime() : 0;
}

function isAtHQ(call) {
  return (
    call?.patientAtHQ === true ||
    call?.status === "arrivedHQ" ||
    !!call?.hq?.bedNumber ||
    !!call?.hq?.firstName
  );
}

function hasUnitsOnCall(call) {
  return (
    (Array.isArray(call?.assignedUnitIds) && call.assignedUnitIds.length > 0) ||
    Object.keys(call?.unitStatuses || {}).length > 0
  );
}

function hasActiveHospitalTransport(call) {
  return (
    !!call?.hospitalTransport?.requested ||
    !!call?.hospitalTransport?.ambulanceUnitId ||
    !!call?.ambulanceUnitId
  );
}

function hasActiveCourtesyTransport(call) {
  return call?.callType === "courtesyTransport";
}

function isHQCourtesyAwaitingUnit(call) {
  return (
    call?.callType === "courtesyTransport" &&
    (!Array.isArray(call?.assignedUnitIds) ||
      call.assignedUnitIds.length === 0)
  );
}

function isUnacknowledgedHQTransport(call) {
  return (
    (!!call?.hospitalTransport?.requested &&
      call?.hospitalTransport?.acknowledged === false &&
      !call?.hospitalTransport?.ambulanceUnitId &&
      !call?.ambulanceId) ||
    (call?.callType === "courtesyTransport" &&
      (!Array.isArray(call?.assignedUnitIds) ||
        call.assignedUnitIds.length === 0))
  );
}

function getQueueTimestamp(call) {
  return (
    call?.requestedAt ||
    call?.queuedAt ||
    call?.createdAt
  );
}

function isHQTransportRequest(call) {
  return hasActiveHospitalTransport(call) || hasActiveCourtesyTransport(call);
}

function isHQAmbulanceAwaitingUnit(call) {
  return (
    !!call?.hospitalTransport?.requested &&
    !call?.hospitalTransport?.ambulanceUnitId &&
    !call?.ambulanceUnitId
  );
}

function isVisibleQueuedCall(call) {
  if (!call || ["completed", "cancelled"].includes(call.status)) return false;

  // HQ courtesy/ambulance requests should show in Live Calls, not the bottom queue
  if (isHQAmbulanceAwaitingUnit(call)) return false;
  if (isHQCourtesyAwaitingUnit(call)) return false;

  return call.status === "queued";
}

function isVisibleLiveCall(call) {
  if (!call || ["completed", "cancelled"].includes(call.status)) return false;

  if (
  call.callType === "emergency" &&
  call.hq?.courtesyRequested === true &&
  !call.hospitalTransport?.requested
) {
  return false;
}

  // Show HQ courtesy/ambulance requests in the main Live Calls window
  if (isHQAmbulanceAwaitingUnit(call)) return true;
  if (isHQCourtesyAwaitingUnit(call)) return true;

  if (call.status === "queued") return false;

  const atHQ = isAtHQ(call);
  const unitsOnCall = hasUnitsOnCall(call);
  const hqTransport = isHQTransportRequest(call);

  if (atHQ && !unitsOnCall && !hqTransport) return false;

  return true;
}

function sortQueuedCalls(a, b) {
  return tsMillis(getQueueTimestamp(b)) - tsMillis(getQueueTimestamp(a));
}

function playHQRequestTone() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.03, now);
    master.connect(ctx.destination);

    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(880, now);
    osc1.connect(master);
    osc1.start(now);
    osc1.stop(now + 0.11);

    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(1175, now + 0.13);
    osc2.connect(master);
    osc2.start(now + 0.13);
    osc2.stop(now + 0.26);

    setTimeout(() => {
      try {
        ctx.close();
      } catch {
        // ignore
      }
    }, 400);
  } catch {
    // ignore audio errors
  }
}

function DispatchTab({ to, label, active, disabled = false }) {
  if (disabled) {
    return (
      <span
        style={{
          border: "1px solid #334155",
          borderRadius: 8,
          padding: "5px 10px",
          color: "#64748b",
          background: "#0f172a",
          fontSize: 10,
          fontWeight: 900,
          lineHeight: 1.1,
          whiteSpace: "nowrap",
          opacity: 0.7,
          cursor: "not-allowed",
        }}
        title="Coming soon"
      >
        {label}
      </span>
    );
  }

  return (
    <Link
      to={to}
      style={{
        border: active ? "1px solid #7dd3fc" : "1px solid #334155",
        borderRadius: 8,
        padding: "5px 10px",
        textDecoration: "none",
        color: active ? "#e0f2fe" : "#e2e8f0",
        background: active ? "#14263f" : "#111c2f",
        boxShadow: active ? "inset 0 0 0 1px rgba(125, 211, 252, 0.15)" : "none",
        fontSize: 10,
        fontWeight: 900,
        lineHeight: 1.1,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Link>
  );
}

function CallsPanel({
  title,
  countLabel,
  calls,
  units,
  availableUnits,
  nowMs,
  openUnitMenuKey,
  setOpenUnitMenuKey,
  openAddUnitCallId,
  setOpenAddUnitCallId,
  onLoadQueuedCallIntoForm,
  onResolveInField,
  onCancelCall,
  onAcknowledgeAmbulance,
  onAcknowledgeCourtesy,
  onQueueCourtesy,
  onUpdateUnitOnCall,
  onAddUnitToCall,
  onAddTrip,
  flashingQueueIds = [],
}) {
  const isLive = title === "Live Calls";

  return (
    <section
      style={{
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 10,
        border: "1px solid #1e293b",
        background: "#0f172a",
        boxShadow: "0 8px 24px rgba(2, 6, 23, 0.24)",
      }}
    >
      <div
        style={{
          padding: "5px 8px",
          borderBottom: "1px solid #22314d",
          background: "linear-gradient(180deg, #111c2f 0%, #0d1728 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          flex: "0 0 auto",
        }}
      >
        <h3
          style={{
            margin: 0,
            color: "#f8fafc",
            fontSize: 10,
            lineHeight: 1,
            fontWeight: 900,
          }}
        >
          {title}
        </h3>

        <div
          style={{
            fontSize: 9,
            color: "#94a3b8",
            fontWeight: 800,
            whiteSpace: "nowrap",
            lineHeight: 1,
          }}
        >
          {countLabel}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: 4,
          display: "grid",
          gridTemplateColumns: isLive
            ? "repeat(3, minmax(0, 1fr))"
            : "repeat(2, minmax(0, 1fr))",
          gap: 4,
          alignContent: "start",
          background: "#08111f",
          gridAutoRows: "min-content",
        }}
      >
        {!calls.length && (
          <div
            style={{
              gridColumn: "1 / -1",
              opacity: 0.86,
              fontSize: 10,
              color: "#94a3b8",
              padding: 8,
              border: "1px dashed #334155",
              borderRadius: 8,
              background: "#0f172a",
            }}
          >
            {isLive ? "No active calls." : "No queued calls."}
          </div>
        )}

        {calls.map((call) => {
          const flashing = flashingQueueIds.includes(call.id);

          return (
            <div
              key={call.id}
              style={{
                minWidth: 0,
                borderRadius: 10,
                animation: flashing ? "dispatchQueueFlash 1s ease-in-out infinite" : "none",
                boxShadow: flashing
                  ? "0 0 0 2px rgba(250, 204, 21, 0.25), 0 0 12px rgba(250, 204, 21, 0.12)"
                  : "none",
              }}
            >
              <DispatchCallCard
                call={call}
                units={units}
                availableUnits={availableUnits}
                nowMs={nowMs}
                openUnitMenuKey={openUnitMenuKey}
                setOpenUnitMenuKey={setOpenUnitMenuKey}
                openAddUnitCallId={openAddUnitCallId}
                setOpenAddUnitCallId={setOpenAddUnitCallId}
                onLoadQueuedCallIntoForm={onLoadQueuedCallIntoForm}
                onResolveInField={onResolveInField}
                onCancelCall={onCancelCall}
                onAcknowledgeAmbulance={onAcknowledgeAmbulance}
                onAcknowledgeCourtesy={onAcknowledgeCourtesy}
                onQueueCourtesy={onQueueCourtesy}
                onUpdateUnitOnCall={onUpdateUnitOnCall}
                onAddUnitToCall={onAddUnitToCall}
                onAddTrip={onAddTrip}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

const lockTabButtonStyle = {
  border: "1px solid #fca5a5",
  borderRadius: 8,
  padding: "5px 10px",
  color: "#ffffff",
  background: "#ef4444",
  fontSize: 10,
  fontWeight: 900,
  lineHeight: 1.1,
  whiteSpace: "nowrap",
  cursor: "pointer",
};

function dedupeUnitIds(ids) {
  return [...new Set((Array.isArray(ids) ? ids : []).filter(Boolean))];
}

export default function Dispatch({ onLock }) {
  const [callType, setCallType] = useState("emergency");
  const [location, setLocation] = useState("");
  const [nature, setNature] = useState("");
  const [notes, setNotes] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [passengerCount, setPassengerCount] = useState("");
  const [selectedUnitIds, setSelectedUnitIds] = useState([]);
  const [editingQueuedCallId, setEditingQueuedCallId] = useState(null);
  const [openUnitMenuKey, setOpenUnitMenuKey] = useState("");
  const [openAddUnitCallId, setOpenAddUnitCallId] = useState("");
  const [busy, setBusy] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [hasUnreadChat, setHasUnreadChat] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  function updateSelectedUnitIds(next) {
  setSelectedUnitIds((prev) =>
    dedupeUnitIds(typeof next === "function" ? next(prev) : next)
  );
}

function handleCallTypeChange(nextType) {
  setCallType(nextType);
  setSelectedUnitIds([]);
}

  const previousAlertIdsRef = useRef(new Set());

  const {
    userReady,
    config,
    units,
    calls,
    debug,
    nowMs,
    availableUnits,
    suggested,
  } = useDispatchData({
    callType,
    location,
  });

  const actions = useDispatchActions({
    units,
    availableUnits,
    suggested,
    formState: {
      callType,
      location,
      nature,
      notes,
      pickupLocation,
      dropoffLocation,
      passengerCount,
      selectedUnitIds,
      editingQueuedCallId,
    },
    setBusy,
    resetForm: () => {
      setCallType("emergency");
      setLocation("");
      setNature("");
      setNotes("");
      setPickupLocation("");
      setDropoffLocation("");
      setPassengerCount("");
      setSelectedUnitIds([]);
      setEditingQueuedCallId(null);
    },
  });

  const queuedCalls = useMemo(
    () => calls.filter(isVisibleQueuedCall).sort(sortQueuedCalls),
    [calls]
  );

  const liveCalls = useMemo(
  () =>
    calls
      .filter((call) => isVisibleLiveCall(call) || isVisibleQueuedCall(call))
      .sort(sortLiveCalls),
  [calls]
);

  const flashingQueueIds = useMemo(
    () => queuedCalls.filter(isUnacknowledgedHQTransport).map((c) => c.id),
    [queuedCalls]
  );

  useEffect(() => {
    const nextIds = new Set(flashingQueueIds);
    let shouldPlay = false;

    nextIds.forEach((id) => {
      if (!previousAlertIdsRef.current.has(id)) shouldPlay = true;
    });

    if (shouldPlay) playHQRequestTone();

    previousAlertIdsRef.current = nextIds;
  }, [flashingQueueIds]);

  const unitSummary = useMemo(() => {
    const avail = units.filter((u) => u.status === "AVAIL").length;
    const onCall = units.filter((u) => !!u.currentCallId).length;
    const oos = units.filter((u) => u.status === "OOS").length;
    return { avail, onCall, oos };
  }, [units]);

  if (!userReady) {
    return (
      <div
        style={{
          width: "100vw",
          minHeight: "100dvh",
          marginLeft: "calc(50% - 50vw)",
          background: "#08111f",
          color: "#e2e8f0",
          padding: 16,
        }}
      >
        Loading dispatch console…
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100vw",
        height: "100dvh",
        marginLeft: "calc(50% - 50vw)",
        overflow: "hidden",
        background: "#08111f",
        display: "grid",
        gridTemplateRows: "46px minmax(0, 1fr)",
        color: "#e2e8f0",
      }}
    >
      <style>
  {`
    @keyframes dispatchQueueFlash {
      0%, 100% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(0.992);
        opacity: 0.92;
      }
    }

    @keyframes dispatchChatFlash {
      0%, 100% {
        background: #111c2f;
        border-color: #334155;
      }
      50% {
        background: #78350f;
        border-color: #f59e0b;
      }
    }
  `}
</style>

      <div
        style={{
          padding: "5px 8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          borderBottom: "1px solid #142033",
          background: "linear-gradient(180deg, #0b1324 0%, #08111f 100%)",
          minHeight: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              margin: 0,
              color: "#f8fafc",
              fontSize: 12,
              lineHeight: 1,
              fontWeight: 900,
            }}
          >
            Dispatch Console
          </div>
          <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>
            Units, call entry, live calls, and queue
          </div>
        </div>

        <div
  style={{
    display: "flex",
    gap: 6,
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  }}
>
  <DispatchTab to="/field-access/dispatch" label="Dispatch Console" active />

  <DispatchTab to="/field-access/hq" label="PT Tracker" />

 <DispatchTab
  to="/field-access/status-board"
  label="Status Board"
/>

<button
  type="button"
  onClick={() => {
  setChatOpen(true);
  setHasUnreadChat(false);
  setUnreadCount(0);
}}
  style={{
    border: hasUnreadChat ? "1px solid #f59e0b" : "1px solid #334155",
    borderRadius: 8,
    padding: "5px 10px",
    color: "#ffffff",
    background: hasUnreadChat ? "#78350f" : "#111c2f",
    fontSize: 10,
    fontWeight: 900,
    lineHeight: 1.1,
    whiteSpace: "nowrap",
    cursor: "pointer",
    animation:
      hasUnreadChat && !chatOpen
        ? "dispatchChatFlash 1s ease-in-out infinite"
        : "none",
  }}
>
  Chat{unreadCount > 0 ? ` (${unreadCount})` : ""}
</button>

<button type="button" onClick={onLock} style={lockTabButtonStyle}>
  Lock
</button>

</div>
      </div>

      <div
        style={{
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: "168px 300px minmax(0, 1fr)",
          gap: 6,
          padding: "6px 8px 8px",
          overflow: "hidden",
          alignItems: "stretch",
        }}
      >
        <DispatchConsolePanel
          mode="units"
          config={config}
          units={units}
          debug={debug}
          busy={busy}
          unitSummary={unitSummary}
          callType={callType}
          setCallType={handleCallTypeChange}
          location={location}
          setLocation={setLocation}
          nature={nature}
          setNature={setNature}
          notes={notes}
          setNotes={setNotes}
          pickupLocation={pickupLocation}
          setPickupLocation={setPickupLocation}
          dropoffLocation={dropoffLocation}
          setDropoffLocation={setDropoffLocation}
          passengerCount={passengerCount}
          setPassengerCount={setPassengerCount}
          selectedUnitIds={selectedUnitIds}
          setSelectedUnitIds={updateSelectedUnitIds}
          editingQueuedCallId={editingQueuedCallId}
          availableUnits={availableUnits}
          suggested={suggested}
          onCreateOrUpdateCall={actions.createOrUpdateCall}
          onToggleUnitService={actions.toggleUnitService}
          onReset={actions.resetForm}
        />

        <DispatchConsolePanel
          mode="entry"
          config={config}
          units={units}
          debug={debug}
          busy={busy}
          unitSummary={unitSummary}
          callType={callType}
          setCallType={handleCallTypeChange}
          location={location}
          setLocation={setLocation}
          nature={nature}
          setNature={setNature}
          notes={notes}
          setNotes={setNotes}
          pickupLocation={pickupLocation}
          setPickupLocation={setPickupLocation}
          dropoffLocation={dropoffLocation}
          setDropoffLocation={setDropoffLocation}
          passengerCount={passengerCount}
          setPassengerCount={setPassengerCount}
          selectedUnitIds={selectedUnitIds}
          setSelectedUnitIds={updateSelectedUnitIds}
          editingQueuedCallId={editingQueuedCallId}
          availableUnits={availableUnits}
          suggested={suggested}
          onCreateOrUpdateCall={actions.createOrUpdateCall}
          onToggleUnitService={actions.toggleUnitService}
          onReset={actions.resetForm}
        />

        <div
          style={{
            minHeight: 0,
            height: "100%",
            display: "grid",
           gridTemplateRows: "minmax(0, 1fr)",
            gap: 6,
            overflow: "hidden",
          }}
        >
          <CallsPanel
            title="Live Calls"
            countLabel={`${liveCalls.length} active`}
            calls={liveCalls}
            units={units}
            availableUnits={availableUnits}
            nowMs={nowMs}
            openUnitMenuKey={openUnitMenuKey}
            setOpenUnitMenuKey={setOpenUnitMenuKey}
            openAddUnitCallId={openAddUnitCallId}
            setOpenAddUnitCallId={setOpenAddUnitCallId}
            onLoadQueuedCallIntoForm={(call) => {
              setEditingQueuedCallId(call.id);
              setCallType(call.hq?.courtesyRequested ? "courtesyTransport" : (call.callType || "emergency"));
              setLocation(call.callType === "courtesyTransport" ? "" : call.location || "");
              setNature(call.callType === "courtesyTransport" ? "" : call.nature || "");
              setNotes(call.notes || "");
              setPickupLocation(call.pickupLocation || call.hq?.courtesyPickupLocation || "");
              setDropoffLocation(call.dropoffLocation || call.hq?.courtesyDestination || "");
              setPassengerCount(
                call.passengerCount || call.hq?.courtesyPassengerCount || ""
              );
              updateSelectedUnitIds(call.assignedUnitIds || call.recommendedUnitIds || []);
            }}
            onResolveInField={actions.resolveInField}
            onCancelCall={actions.cancelCall}
            onAcknowledgeAmbulance={actions.acknowledgeAmbulanceRequest}
            onAcknowledgeCourtesy={actions.acknowledgeCourtesyRequest}
            onQueueCourtesy={actions.queueCourtesyRequest}
            onUpdateUnitOnCall={actions.updateUnitOnCall}
            onAddUnitToCall={actions.addUnitToCall}
            onAddTrip={actions.addTripToCall}
          />
        </div>
            </div>

      <DispatchChat
        open={chatOpen}
        setOpen={setChatOpen}
        unreadCount={unreadCount}
        setUnreadCount={setUnreadCount}
        setHasUnreadChat={setHasUnreadChat}
      />
    </div>
  );
}